/**
 * markdownText — collapse a markdown source string into a clean, single-line
 * PLAIN-TEXT preview. Used by list/card previews (e.g. the Notes list) where the
 * backend hands back the raw markdown `body`/`excerpt` and we must show a human
 * summary, NOT literal `# Heading` / `**bold**` / `` `code` `` syntax.
 *
 * This is deliberately a lightweight de-marker — not a CommonMark parser. It
 * strips the common inline + block markers, unwraps links/images to their text,
 * and flattens all whitespace to single spaces. For a faithful RICH rendering
 * (headings, lists, emphasis) the note editor already paints the server's
 * `body_html`; this helper is only for the terse, one-line card excerpt.
 */

/** Strip markdown syntax from `src`, returning a trimmed single-line summary. */
export function stripMarkdown(src: string | null | undefined): string {
  if (!src) return "";
  let s = src;

  // Fenced code blocks ```…``` and ~~~…~~~ → keep inner text, drop the fences.
  s = s.replace(/```+[^\n]*\n?([\s\S]*?)```+/g, "$1");
  s = s.replace(/~~~+[^\n]*\n?([\s\S]*?)~~~+/g, "$1");

  // Images ![alt](url) → alt; links [text](url) / [text][ref] → text.
  s = s.replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1");
  s = s.replace(/\[([^\]]*)\]\([^)]*\)/g, "$1");
  s = s.replace(/\[([^\]]*)\]\[[^\]]*\]/g, "$1");

  // Autolinks <https://…> → the bare URL text.
  s = s.replace(/<((?:https?|mailto):[^>]+)>/g, "$1");

  // Line-leading block markers: ATX headings (#), blockquotes (>), list bullets
  // (-, *, +), ordered-list numbers (1.), and horizontal rules (---, ***, ___).
  s = s.replace(/^[ \t]*#{1,6}[ \t]+/gm, ""); // headings
  s = s.replace(/^[ \t]*>[ \t]?/gm, ""); // blockquotes
  s = s.replace(/^[ \t]*(?:[-*+]|\d+[.)])[ \t]+/gm, ""); // list items
  s = s.replace(/^[ \t]*(?:[-*_])(?:[ \t]*[-*_]){2,}[ \t]*$/gm, ""); // hr

  // Inline emphasis / strong / strikethrough markers (leave the wrapped text).
  s = s.replace(/(\*\*\*|___)(.*?)\1/g, "$2");
  s = s.replace(/(\*\*|__)(.*?)\1/g, "$2");
  s = s.replace(/(\*|_)(.*?)\1/g, "$2");
  s = s.replace(/~~(.*?)~~/g, "$1");

  // Inline code `code` → code.
  s = s.replace(/`+([^`]*)`+/g, "$1");

  // Table pipes / leftover markers, then flatten all whitespace to single space.
  s = s.replace(/\|/g, " ");
  s = s.replace(/\s+/g, " ").trim();

  return s;
}
